import {
  FlagIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ScaleIcon,
  SparklesIcon,
  MegaphoneIcon as SpeakerphoneIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import Image from "next/legacy/image";

import { Logo, blurDataURL } from "../lib/constants";
import { getAboutPage } from "../lib/api";
import { imageBuilder } from "../lib/sanity";
import BlockContent from "../components/block-content";
import DeliveryMethods from "../components/delivery-methods";
import Meta from "../components/meta";
import { motion } from "framer-motion";
import { Logo50Square, LogoText50 } from "@/components/anniversary-assets";

import { cn } from "@/lib/utils";

const DynamicIcon = ({ iconName, ...props }) => {
  const icons = {
    FlagIcon,
    UserGroupIcon,
    GlobeAltIcon,
    ScaleIcon,
    SparklesIcon,
    SpeakerphoneIcon,
    UsersIcon,
  };
  const IconComponent = icons[iconName];
  return <IconComponent {...props} />;
};

const HistorySection = ({ data }) => {
  return (
    <div className="bg-white overflow-hidden">
      <div className="relative max-w-7xl mx-auto pt-10 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="hidden lg:block absolute top-0 bottom-0 left-3/4 w-screen" />
        <div className="mx-auto text-base max-w-prose lg:grid lg:grid-cols-2 lg:gap-8 lg:max-w-none">
          <div>
            <h2 className="text-base text-accent-600 font-semibold tracking-wide uppercase">
              {data.category}
            </h2>
            <h3 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {data.title}
            </h3>
          </div>
        </div>
        <div className="mt-6 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="relative lg:row-start-1 lg:col-start-2">
            <svg
              className="hidden lg:block absolute top-0 right-0 -mt-20 -mr-20"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384"
              aria-hidden="true"
            >
              <defs>
                <pattern
                  id="de316486-4a29-4312-bdfc-fbce2132a2c1"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#de316486-4a29-4312-bdfc-fbce2132a2c1)"
              />
            </svg>
            <div className="relative mx-auto max-w-prose lg:max-w-none">
              <div className="aspect-w-12 aspect-h-7 lg:aspect-w-37 lg:aspect-h-43 shadow-lg rounded-lg">
                <Image
                  alt="Header Image"
                  draggable={false}
                  layout="fill"
                  placeholder="blur"
                  className="rounded-lg object-cover object-center"
                  blurDataURL={blurDataURL}
                  src={imageBuilder(data.image)
                    .width(592)
                    .height(688)
                    .dpr(2)
                    .crop("entropy")
                    .url()}
                />
              </div>
            </div>
          </div>
          <div className="mt-8 lg:mt-0">
            <div className="mt-0 mx-auto lg:max-w-none lg:row-start-1 lg:col-start-1">
              <BlockContent
                className="prose prose-accent text-gray-500"
                content={data.content}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ValuesSection = ({ data }) => {
  return (
    <div className="bg-accent-700">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 sm:pt-20 sm:pb-24 lg:max-w-7xl lg:pt-24 lg:px-8">
        <h2 className="text-3xl font-extrabold text-white tracking-tight">
          {data.title}
        </h2>
        <p className="mt-4 max-w-3xl text-lg text-accent-200 whitespace-pre-line">
          {data.description}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:mt-16 lg:grid-cols-4 lg:gap-x-8 lg:gap-y-16">
          {data.items.map((feature) => (
            <div key={feature.title}>
              <div>
                <span className="flex items-center justify-center h-12 w-12 rounded-md bg-white bg-opacity-10">
                  <DynamicIcon
                    iconName={feature.icon}
                    className="h-6 w-6 text-white"
                  />
                </span>
              </div>
              <div className="mt-6">
                <h3 className="text-lg font-medium text-white">
                  {feature.title}
                </h3>
                <p className="mt-2 text-base text-accent-200">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const fullSvgLogo = (
  <svg
    className="md:max-w-[90%]"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 346 95"
  >
    <path
      className="fill-accent"
      d="M6.1 61.9H.5v32.2h34.8v-5.6H6.1V61.9ZM41.3.3 6.1 35.4h53.1v53.1l35.1-34.8V.3h-53Z"
    />
    <path
      className="fill-accent"
      d="M22.2 53.6c2.4 0 7.1.8 6.6 3.3l-.6 3.1h-4.5l.2-1.2c.2-.9-1.2-1-1.8-1-3.5 0-5 3.3-5 5.5 0 1.9.9 3.6 3.3 3.6 1.1 0 1.9-.2 2-.9l.2-1.2h4.5l-.5 2.8c-.6 2.9-4.3 3.6-6.7 3.6-4.8 0-8.2-3.1-8.2-7.6.2-5.4 4.7-10 10.5-10Zm14.2-.5h4.3L31 72h-4.4l9.8-18.9Zm7 11.4-.2 1.1c-.2 1 1.1 1.2 2.1 1.2 1.4 0 1.9-.7 1.9-1.3 0-1.5-7.1-1.1-7.1-6.3 0-4.1 3.6-5.6 7.4-5.6 2.1 0 6.3.6 5.7 3.8l-.5 2.4h-4.5l.2-1.1c.1-.6-.6-.8-1.4-.8-1.3 0-1.7.5-1.7 1 0 2 7.1 1.1 7.1 6.4 0 4-3.6 5.9-7.7 5.9-2.5 0-6.8-1.2-6.2-4.8l.4-2h4.5v.1Zm138.1 8.9h1.1v-6.7h-1.8v1.4h-1.5v-2.8h8.3v2.8h-1.5v-1.4h-1.7v6.7h1.1v1.3h-4v-1.3Zm6.8.1h1v-6.9h-1.1v-1.3h2.7v3.9c.3-.7 1.2-1.4 2.3-1.4 1.5 0 2.4.8 2.4 2.6v3.1h1v1.3h-2.7v-4c0-.8-.2-1.4-1.1-1.4-1.2 0-1.9 1-1.9 2.3v1.9h1v1.3h-3.6v-1.4Zm12.9-2.9c0-.9-.6-1.5-1.3-1.5-.9 0-1.5.6-1.6 1.5h2.9Zm-1.3-2.8c2 0 3 1.4 3 3.2v.6h-4.7c.1 1.2 1 1.9 2 1.9.6 0 1-.2 1.4-.4.2-.1.4-.2.6-.4l.7 1.2c-.2.2-.5.3-.7.5-.5.3-1.2.5-2 .5-2.3 0-3.6-1.6-3.6-3.6-.1-2 1.3-3.5 3.3-3.5Zm10.8 5.7c.9 0 1.7-.8 1.7-2.1s-.7-2.1-1.7-2.1c-.9 0-1.7.7-1.7 2.1 0 1 .6 2.1 1.7 2.1Zm-3.3 2.6v-6.6c0-.2-.1-.3-.3-.3h-.8v-1.3h1.7c.6 0 .8.3.8.6v.3c.1-.2.3-.4.5-.6.4-.3.9-.6 1.7-.6 1.8 0 3 1.5 3 3.6 0 2.2-1.3 3.6-3.1 3.6-.7 0-1.2-.2-1.5-.5-.2-.1-.3-.3-.5-.5V76h1v1.3h-3.7V76h1.2v.1Zm9.9-2.5c.9 0 1.6-1 1.6-1.8v-.2h-.3c-.7 0-2.3.1-2.3 1.1 0 .5.4.9 1 .9Zm1.1-3.1h.4v-.2c0-.9-.5-1.2-1.3-1.2-.5 0-1 .2-1.3.3l-.6.3-.6-1.2c.2-.2.5-.3.7-.4.5-.2 1.1-.4 2-.4 1.8 0 2.8.9 2.8 2.7v2.7c0 .2.1.3.3.3h.6v1.3h-1.6c-.6 0-.9-.3-.9-.7v-.5c-.1.2-.3.5-.5.7-.4.3-.9.7-1.7.7-1.2 0-2.3-.7-2.3-2.1.2-2 2.9-2.3 4-2.3Zm3.6 3h1v-4c0-.2-.1-.3-.3-.3h-.8v-1.3h1.8c.6 0 .9.3.9.8v.8c.3-1 1.1-1.7 2.2-1.7h.3v1.7h-.4c-1.4 0-2 1.2-2 2.5v1.5h1v1.3H222v-1.3Zm6.7-4.3h-1v-1.3h1.1V66h1.6v1.9h1.5v1.3h-1.5v2.9c0 1.2.9 1.3 1.4 1.3h.3v1.4h-.5c-1 0-2.8-.3-2.8-2.6v-3h-.1Zm4 4.3h1v-4c0-.2-.1-.3-.3-.3h-.8v-1.3h1.8c.6 0 .9.3.9.8v.6c.1-.2.8-1.5 2.5-1.5 1.5 0 2.3.8 2.3 2.6v3.1h1v1.3h-2.7v-4c0-.8-.2-1.4-1.1-1.4-1.2 0-1.9 1.2-1.9 2.4v1.8h1v1.3h-3.6v-1.4h-.1Zm13-2.9c0-.9-.6-1.5-1.3-1.5-.9 0-1.5.6-1.6 1.5h2.9Zm-1.3-2.8c2 0 3 1.4 3 3.2v.6h-4.7c.1 1.2 1 1.9 2 1.9.6 0 1-.2 1.4-.4.2-.1.4-.2.6-.4l.7 1.2c-.2.2-.5.3-.7.5-.5.3-1.2.5-2 .5-2.3 0-3.6-1.6-3.6-3.6-.2-2 1.3-3.5 3.3-3.5Zm3.5 5.7h1v-4c0-.2-.1-.3-.3-.3h-.8v-1.3h1.8c.6 0 .9.3.9.8v.8c.3-1 1.1-1.7 2.2-1.7h.3v1.7h-.4c-1.4 0-2 1.2-2 2.5v1.5h1v1.3H248v-1.3h-.1Zm9.1-4.3h-1v-1.3h1.1V66h1.6v1.9h1.5v1.3h-1.5v2.9c0 1.2.9 1.3 1.4 1.3h.3v1.4h-.5c-1 0-2.8-.3-2.8-2.6v-3h-.1Zm7.3 4.3c1.1 0 2-.9 2-2.1 0-1.3-.9-2.1-2-2.1s-2 .9-2 2.1c0 1.2.9 2.1 2 2.1Zm0-5.7c2.1 0 3.7 1.5 3.7 3.6s-1.7 3.6-3.7 3.6c-2.1 0-3.7-1.5-3.7-3.6s1.7-3.6 3.7-3.6Zm11.1 5.7c.9 0 1.7-.8 1.7-2.1s-.7-2.1-1.7-2.1c-.9 0-1.7.7-1.7 2.1 0 1 .6 2.1 1.7 2.1Zm-3.3-7h-1v-1.3h2.7v3.5c.1-.2.3-.4.5-.5.3-.3.9-.5 1.6-.5 1.9 0 3 1.5 3 3.6 0 2.2-1.3 3.6-3.1 3.6-.7 0-1.3-.3-1.6-.5l-.5-.5v.9h-1.6v-8.3Zm7.9 2.7h-1v-1.3h2.7V72c0 .8.2 1.4 1.1 1.4 1.2 0 1.9-1.2 1.9-2.4v-1.8h-1v-1.3h2.7v5.2c0 .2.1.3.3.3h.7v1.3h-1.7c-.6 0-.9-.3-.9-.8v-.6c-.1.2-.8 1.5-2.5 1.5-1.3 0-2.2-.7-2.2-2.6v-3h-.1Zm8.9-3.9h1.7v1.5h-1.7v-1.5Zm-1 8.2h1v-4.3h-1.1v-1.3h2.7v5.5h1v1.3h-3.6v-1.2Zm4.3-7h-1.1v-1.3h2.7v7.2c0 .6.1.9.7.9h.3v1.4h-.6c-1.1 0-2.1-.4-2.1-2.2v-6h.1Zm6.3 7c.9 0 1.7-.6 1.7-2.1 0-1.1-.5-2.1-1.7-2.1-1 0-1.7.8-1.7 2.1 0 1.2.7 2.1 1.7 2.1Zm-.3-5.7c.7 0 1.2.2 1.5.5.2.1.3.3.4.5v-2.1H299v-1.3h2.7v7.9c0 .2.1.3.3.3h.6v1.3H301c-.6 0-.9-.3-.9-.7v-.4c-.1.2-.3.4-.5.6-.4.3-.9.6-1.7.6-1.8 0-3-1.5-3-3.6.2-2.2 1.5-3.6 3.3-3.6Zm7.7.1h3.3v1.3h-.8l.9 3.3c0 .2.1.3.1.5v.2-.2c0-.1 0-.3.1-.5l1.3-4.6h1.5l1.3 4.6c0 .2.1.3.1.5v.2-.2c0-.1 0-.3.1-.5l.9-3.3h-.8v-1.3h3.3v1.3h-.8l-1.6 5.5h-1.9l-1.2-4c0-.2-.1-.4-.1-.5V70v.2c0 .1 0 .3-.1.5l-1.2 4h-1.9l-1.6-5.5h-.8v-1.3h-.1Zm12.9-2.6h1.7v1.5h-1.7v-1.5Zm-1 8.2h1v-4.3h-1.1v-1.3h2.7v5.5h1v1.3h-3.6v-1.2Zm4.3-4.3h-1v-1.3h1.1V66h1.6v1.9h1.5v1.3h-1.5v2.9c0 1.2.9 1.3 1.4 1.3h.3v1.4h-.5c-1 0-2.8-.3-2.8-2.6v-3h-.1Zm4.1 4.3h1v-6.9h-1.1v-1.3h2.7v3.9c.3-.7 1.2-1.4 2.3-1.4 1.5 0 2.4.8 2.4 2.6v3.1h1v1.3h-2.7v-4c0-.8-.2-1.4-1.1-1.4-1.2 0-1.9 1-1.9 2.3v1.9h1v1.3h-3.6v-1.4Zm9.2-.5h1.7v1.7h-1.7V73Zm1.3-6.3s.2.2.5.2c.2 0 .4-.1.4-.3 0-.5-1-.4-1-1 0-.3.3-.6.7-.6.4 0 .6.2.6.2l-.1.2s-.2-.2-.5-.2c-.2 0-.4.1-.4.3 0 .4 1 .3 1 1 0 .3-.3.6-.7.6-.5 0-.7-.3-.7-.3l.2-.1Zm1.8-1.7h.3l.5 1.1c0 .1.1.3.1.3s.1-.2.1-.3l.5-1.1h.3l.2 2.1h-.3l-.1-1.3v-.3s-.1.2-.1.3l-.4 1h-.3l-.4-1c0-.1-.1-.3-.1-.3v.3l-.1 1.3h-.3l.1-2.1Z"
    />
    <path
      fill="#18181b"
      d="M114.4 1c2.4 0 7.5.9 7.5 3.9v2.4h-2.6V5.8c0-1.7-3-2.4-4.8-2.4-4.2 0-7.3 3.1-7.3 7.6 0 4.7 3.2 8 7.5 8 1.4 0 4.9-.5 4.9-2.3v-1.5h2.6v2.4c0 2.9-4.9 3.9-7.7 3.9-5.9 0-10.2-4.6-10.2-10.3 0-5.9 4.4-10.2 10.1-10.2Zm8.6 17.9h2.5V3.6H123V1.4h7.8v2.2h-2.5v15.2h6.9v-3.2h2.4v5.5H123v-2.2Zm28.8-6.6-2.4-6.8c-.3-.8-.3-1.5-.3-1.5h-.1s-.1.7-.3 1.5l-2.4 6.8h5.5Zm-12.5 6.6h1.9l6.3-17.5h3.2l6.3 17.5h1.9v2.2h-6.8v-2.2h2.1l-1.5-4.4h-7l-1.5 4.4h2.1v2.2h-6.8v-2.2h-.2Zm30.5-8.1c2.2 0 3.6-1.4 3.6-3.7 0-1.5-.6-2.6-1.8-3.1-.6-.3-1.5-.4-2.4-.4h-3.3v7.2h3.9Zm-9.2 8.1h2.5V3.5h-2.5V1.3h8.5c1.4 0 2.5.1 3.6.4 2.1.7 3.4 2.4 3.4 5.1 0 2.8-1.6 4.9-4.1 5.5v.1s.5.3.8.9l2.8 4.9c.3.5.7.6 1.4.6h.6V21H176c-1.7 0-2.1-.3-2.7-1.5l-3-5.4c-.5-.8-.9-1.1-2.1-1.1h-2.3v5.8h2.4V21h-7.7v-2.1Zm19 0h2.5V3.5h-2.5V1.3h7.4v2.2h-2.2v6.2h2.5l4.3-6.2h-1.8V1.3h6.7v2.2h-1.9l-5.1 7.1v.1s.6.3 1.1 1.3l3.5 6.2c.3.5.7.7 1.4.7h.9v2.2h-2c-1.6 0-2.1-.4-2.7-1.5l-3.8-6.8c-.3-.6-.7-.8-1.5-.8h-1.6v6.9h2.4v2.2h-7.7v-2.2h.1ZM205.2.3h2.4l-7.1 21.9h-2.4L205.2.3Zm6.5 14.9v1.5c0 1.5 1.9 2.3 3.9 2.3 2.3 0 3.9-1.1 3.9-3 0-2.3-2.3-3-4.7-4-2.6-1-5.3-2.1-5.3-5.6 0-3.7 3-5.3 6.3-5.3 3 0 6 1.1 6 3.3v2.3h-2.6V5.3c0-1.1-1.8-1.8-3.4-1.8-1.9 0-3.5.9-3.5 2.7 0 2.1 2 2.8 4.2 3.6 2.9 1.1 5.8 2.2 5.8 5.9 0 3.8-3.1 5.7-6.7 5.7-3.1 0-6.5-1.4-6.5-4.2v-2.1h2.6v.1Zm13.8-11.7H223V1.3h7.8v2.2h-2.5v10.2c0 1.3.3 2.4.8 3.2.8 1.3 2.3 2 4.2 2 1.9 0 3.4-.7 4.2-2.1.5-.8.8-1.9.8-3.2V3.5h-2.5V1.3h7.7v2.2H241v10.2c0 1.9-.5 3.6-1.4 4.8-1.3 1.9-3.7 2.9-6.4 2.9-2.8 0-5.1-1.1-6.5-2.9-.9-1.3-1.3-2.9-1.3-4.8V3.5h.1Zm19.8 15.4h2.5V3.6h-2.5V1.4h7.8v2.2h-2.5v15.2h6.9v-3.2h2.4v5.5h-14.6v-2.2Zm16.3 0h2.5V3.6h-2.5V1.4h7.8v2.2h-2.5v15.2h6.9v-3.2h2.4v5.5h-14.6v-2.2Zm16.3 0h2.5V3.5h-2.5V1.4h7.8v2.1h-2.5v15.4h2.5v2.2h-7.8v-2.2Zm11.5-15.4h-1.9V1.3h7v2.2h-2.2l4.4 13c.2.7.3 1.5.3 1.5h.1s.1-.8.3-1.5l4.4-13h-2.2V1.3h7v2.2h-2l-6.2 17.6h-3l-6-17.6Zm26.2 8.8-2.4-6.8c-.3-.8-.3-1.5-.3-1.5h-.1s-.1.7-.3 1.5l-2.4 6.8h5.5Zm-12.5 6.6h1.9l6.3-17.5h3.2l6.3 17.5h1.9v2.2h-6.8v-2.2h2.1l-1.5-4.4h-7l-1.5 4.4h2.1v2.2h-6.8v-2.2h-.2Zm21.3 0h2.5V3.5h-2.5V1.3h5.2l9.7 14.1.7 1.4h.1s-.1-1-.1-1.6V3.5h-2.5V1.3h7.8v2.2h-2.5v17.6H340L330.3 7c-.4-.6-.7-1.4-.7-1.4h-.1s.1 1 .1 1.6V19h2.5v2.2h-7.8v-2.3h.1ZM114.6 33.4c4.2 0 8.6 1.4 8.6 4.9v3.3H117v-1c0-.7-.7-1-2-1-2.5 0-3.1 1.6-3.1 3.3 0 2.8 1.7 3.8 3.8 3.8 1.3 0 3.4-.3 5.4-1.9l2.4 5.2c-2.5 2.3-5.8 2.8-8.3 2.8-5.5 0-10.7-3.5-10.7-9.6-.2-5.3 3.9-9.8 10.1-9.8Zm20.8 13.2c1.7 0 3.2-1.2 3.2-3.4 0-2.3-1.5-3.6-3.2-3.6-1.7 0-3.2 1.3-3.2 3.6 0 2.2 1.5 3.4 3.2 3.4Zm0-13.2c6 0 10.7 3.9 10.7 9.7 0 5.8-4.8 9.6-10.7 9.6-5.9 0-10.7-3.7-10.7-9.6 0-5.8 4.8-9.7 10.7-9.7ZM157.5 47h2.1v5.3H148V47h2.1v-7.1c0-.5-.2-.8-.8-.8h-1.5v-5.3h6.6c1.7 0 2.7.9 2.7 2.2v.9h.1c.3-.7.7-1.2 1.3-1.8 1-.9 2.4-1.7 4.7-1.7 3.1 0 6.2 1.4 6.2 6.8V47h2.1v5.3H162V41.7c0-1.2-.5-1.7-1.4-1.7-2.2 0-3 2.2-3 4.2V47h-.1Zm15.4-1.5h6.2v.8c0 .5.4.9 1.8.9s1.7-.4 1.7-.8c0-.8-.9-.8-3.2-1.6-2.9-1-5.9-2.3-5.9-5.8 0-4.4 4.9-5.6 8.3-5.6 3.2 0 7.5 1 7.5 4.2v2.6h-6.2v-.6c0-.3-.2-.6-1.1-.6-1 0-1.2.3-1.2.7 0 .9 1.6 1.2 2.8 1.5 2.8.7 6.3 1.9 6.3 5.5 0 4.1-4.5 6-8.7 6-3.6 0-8.4-1.4-8.4-4.5v-2.7h.1Zm20-6.4h-2.2v-5.3h2.5v-4.9h7.2v4.9h3.6v5.3h-3.6v5.5c0 1.5 1.9 1.8 3 1.8h.8v5.9l-.5.1h-1.4c-3.2 0-9.4-.9-9.4-7v-6.3Zm13.5 7.9h2.4v-7.1c0-.5-.2-.8-.8-.8h-1.5v-5.3h6.6c1.7 0 2.7.6 2.7 2.5V38h.1c.7-2.4 3.2-4.5 5.9-4.5h.6v7.3l-.4-.1h-1c-3.3 0-4.8 1.4-4.8 4.6V47h2.4v5.3h-12.2V47Zm31.1-7.9h-2.2v-5.3h9.7v12.4c0 .5.2.8.8.8h1.4v5.3h-6.5c-1.8 0-2.7-.7-2.7-2.3v-.9h-.1c-.2.7-.6 1.2-1.2 1.8-.9.9-2.3 1.8-4.7 1.8-3 0-6.2-1.5-6.2-6.8v-6.8h-2.2v-5.3h9.7v10.7c0 1.2.4 1.7 1.4 1.7 2 0 2.9-2.1 2.9-4.5v-2.6h-.1Zm21.3-5.7c4.2 0 8.6 1.4 8.6 4.9v3.3h-6.2v-1c0-.7-.7-1-2-1-2.5 0-3.1 1.6-3.1 3.3 0 2.8 1.7 3.8 3.8 3.8 1.3 0 3.4-.3 5.4-1.9l2.4 5.2c-2.5 2.3-5.8 2.8-8.3 2.8-5.5 0-10.7-3.5-10.7-9.6-.1-5.3 3.9-9.8 10.1-9.8Zm12.4 5.7H269v-5.3h2.5v-4.9h7.2v4.9h3.6v5.3h-3.6v5.5c0 1.5 1.9 1.8 3 1.8h.8v5.9l-.5.1h-1.4c-3.2 0-9.4-.9-9.4-7v-6.3Zm15.2-12.4h7.1v5.4h-7.1v-5.4ZM284.2 47h2.1v-7.9H284v-5.3h9.7V47h2.1v5.3h-11.6V47Zm23.2-.4c1.7 0 3.2-1.2 3.2-3.4 0-2.3-1.5-3.6-3.2-3.6-1.7 0-3.2 1.3-3.2 3.6 0 2.2 1.5 3.4 3.2 3.4Zm0-13.2c6 0 10.7 3.9 10.7 9.7 0 5.8-4.8 9.6-10.7 9.6-5.9 0-10.7-3.7-10.7-9.6 0-5.8 4.8-9.7 10.7-9.7ZM329.5 47h2.1v5.3H320V47h2.1v-7.1c0-.5-.3-.8-.8-.8h-1.5v-5.3h6.6c1.7 0 2.7.9 2.7 2.2v.9h.1c.3-.7.7-1.2 1.3-1.8 1-.9 2.4-1.7 4.7-1.7 3.1 0 6.2 1.4 6.2 6.8V47h2.1v5.3H334V41.7c0-1.2-.5-1.7-1.4-1.7-2.2 0-3 2.2-3 4.2V47h-.1Z"
    />
  </svg>
);

function TimelineSection({ data }) {
  const hasHash =
    typeof window !== "undefined" &&
    window.location.hash &&
    window.location.hash !== "#history";

  return (
    <div
      className="max-w-7xl mx-auto pt-10 pb-0 px-4 sm:px-6 lg:px-8 scroll-m-11 py-16 sm:pt-20"
      id="history"
    >
      <div className="lg:grid lg:grid-cols-2 lg:gap-36 mb-16 md:mb-24">
        <div>
          <h2 className="text-base text-accent-600 font-semibold tracking-wide uppercase">
            {data.category}
          </h2>
          <h3 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            {data.title}
          </h3>
          <BlockContent
            className="prose prose-accent text-gray-500 mt-6"
            content={data.content}
          />
        </div>

        <div className="lg:flex flex-col justify-center items-center [&>span]:!w-full hidden">
          <div className="hidden md:flex items-center gap-2 w-full">
            <Logo className="text-accent w-[26.5%] flex flex-shrink-0" />
            <LogoText50 className="flex-1" />
          </div>
        </div>
      </div>

      <div className="relative">
        {/* Vertical line */}
        {/* <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-accent-500" /> */}
        <div className="absolute left-[15px] lg:left-1/2 top-0 bottom-0 w-0.5 bg-accent-500 transform lg:-translate-x-1/2" />

        {data.timeline.map((event, index) => {
          const isEven = index % 2 === 0;
          const isLast = index === data.timeline.length - 1;
          return (
            <motion.div
              id={`history-${event.year}`}
              key={event.year}
              initial={hasHash ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              whileInView={
                hasHash ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }
              }
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.5 }}
              className={`flex flex-col lg:flex-row scroll-m-28  ${
                !isEven ? "lg:flex-row-reverse" : ""
              } ${!isLast ? "mb-16 lg:mb-32" : ""}`}
            >
              <div
                className={`flex items-start mb-4 lg:mb-0 lg:w-[calc(50%-1rem)] `}
              >
                <div
                  className={`w-8 flex justify-center lg:hidden sticky top-[74px] ${
                    index !== 0 ? "py-1" : ""
                  }`}
                >
                  <div className="h-7  font-semibold text-sm px-2 text-white flex items-center z-10 justify-center leading-none bg-accent rounded-full">
                    {event.year}
                  </div>
                </div>

                <div
                  className={`w-full pl-6 ${
                    !isEven ? "lg:pl-14" : "lg:pr-14 lg:pl-0"
                  }`}
                >
                  <h3 className="text-2xl lg:text-3xl font-semibold mb-3 text-gray-800">
                    {event.title}
                  </h3>
                  <p className="prose prose-accent text-gray-500">
                    {event.description}
                  </p>
                </div>
              </div>

              <div className="w-8 justify-center hidden lg:flex">
                <div className="h-7 -mt-[2px] sticky top-[74px] font-semibold text-sm px-2 text-white flex items-center z-10 justify-center leading-none bg-accent rounded-full">
                  {event.year}
                </div>
              </div>

              <div
                className={`w-full pl-14 lg:w-[calc(50%-1rem)] justify-center flex ${
                  !isEven ? "lg:pr-14 lg:pl-0" : "lg:pl-14 lg:pr-0"
                } `}
              >
                {event.image && (
                  <div
                    className={cn(
                      "w-full relative",
                      event.imageClassName ? event.imageClassName : "",
                      event.imageSmall
                        ? "max-w-[250px] max-h-[250px] h-[250px] w-[250px]"
                        : "aspect-w-4 aspect-h-3 bg-gray-50"
                    )}
                  >
                    <Image
                      src={imageBuilder(event.image)
                        .width(event.imageSmall ? 250 : 536)
                        .height(event.imageSmall ? 250 : 402)
                        .dpr(2)
                        .crop("entropy")
                        .url()}
                      alt={event.title}
                      width={event.image?.width || 800}
                      height={event.image?.height || 500}
                      layout="fill"
                      className={cn(
                        "rounded-md overflow-hidden w-full",
                        event.imageContain ? "object-contain" : "object-cover",
                        event.imageSmall ? "!max-w-[250px]" : ""
                      )}
                    />
                  </div>
                )}

                {event.logoType === "csLogo" ? fullSvgLogo : null}
                {event.logoType === "50thLogo" ? (
                  <div className="flex items-center justify-center flex-1">
                    <Logo50Square className="flex-1 max-w-[350px]" />
                  </div>
                ) : null}

                {event.imageChildren}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Dotted line */}
      {/* <div className="w-full h-[2px] mt-12 border-gray-300 border-b border-dashed" /> */}

      <div className="lg:pt-36 pt-24 pb-36 relative">
        <div
          className="absolute left-[15px] lg:left-1/2 top-0 bottom-0 w-0.5 transform lg:-translate-x-1/2"
          style={{
            background: `repeating-linear-gradient(
      to bottom,
      rgb(209 213 219),
      rgb(209 213 219) 4px,
      transparent 4px,
      transparent 8px
    )`,
          }}
        />

        <div className="lg:w-[calc(50%-1rem)] pl-14 lg:pl-14">
          <h3 className="mt-2 leading-8 tracking-tight text-2xl lg:text-3xl font-semibold mb-3 text-gray-800 sm:text-4xl">
            {data.conclusionTitle}
          </h3>
          <BlockContent
            className="prose prose-accent text-gray-500 mt-6 pr-6"
            content={data.conclusionContent}
          />
        </div>
      </div>
    </div>
  );
}

const About = ({ data: { seo, about, services, values, history } }) => {
  return (
    <>
      <Meta path="/about" {...seo} />
      <HistorySection data={about} />
      <TimelineSection data={history} />
      <DeliveryMethods className="bg-gray-50" data={services} />
      <ValuesSection data={values} />
    </>
  );
};

export async function getStaticProps({ preview = false }) {
  const data = await getAboutPage(preview);

  return {
    props: { data },
    revalidate: 1,
  };
}

export default About;
